import { SERVICE_TYPES } from '@/constants/serviceTypes';
import { dimensionLabel } from '@/components/Account/ServiceCatalog/utils';
import { getOrderCatalogScope } from '../../../../utilities/serviceCatalog';
import { isServiceLabel } from '../../../../utilities/serviceLines';

// Teammate roles that may quick add (useClientUser expands an admin's roles to all of them).
const QUICK_ADD_TEAMMATE_ROLES = ['admin', 'operations'];

/**
 * Whether this user may quick add a code to the shipper's service catalog from the Services tab.
 * Quick add writes the catalog on the spot and Hasura doesn't limit whose catalog a user writes,
 * so this is the gate: the catalog's own shipper account (an Onward admin impersonating it
 * included) or its admin / operations teammates, never on a read-only order.
 *
 * @param {object} [user] The current user and order context.
 * @param {string} user.accountType `useClientUser` account type.
 * @param {string} user.clientId The user's client id.
 * @param {string} user.shipperId Shipper whose catalog the order uses.
 * @param {boolean} [user.readOnly] Whether the order is read-only.
 * @param {string} [user.userType] `'teammate'` for a teammate login.
 * @param {Object<string, boolean>} [user.teammateRoles] The teammate's roles.
 * @returns {boolean} True when quick add is offered.
 */
export const canQuickAddService = ({ accountType, clientId, shipperId, readOnly, userType, teammateRoles } = {}) =>
    !readOnly &&
    accountType === 'shipper' &&
    !!shipperId &&
    clientId === shipperId &&
    (userType !== 'teammate' || QUICK_ADD_TEAMMATE_ROLES.some((role) => !!teammateRoles?.[role]));

/**
 * Defaults for adding a service line's code to the catalog, catalog-row-shaped for
 * ServiceCatalogModal and scoped to the order's own catalog scope.
 *
 * @param {object} group Grouped service line (`code`, `description`, `type`, `size`, `service_time`).
 * @param {object} order The order being edited.
 * @returns {object} Catalog row defaults.
 */
export const quickAddCatalogDefaults = (group, order) => ({
    service_code: `${group?.code ?? ''}`.trim(),
    description: isServiceLabel(group?.description, group?.code) ? `${group.description}`.trim() : '',
    service_type: SERVICE_TYPES.includes(group?.type) ? group.type : '',
    size: group?.size || '',
    service_time: Number.isFinite(group?.service_time) ? group.service_time : null,
    dimensions: getOrderCatalogScope(order),
});

/**
 * Scope pickers for a quick add: one per order dimension, offering only the order's value, so the
 * new entry is one this order's catalog query returns.
 *
 * @param {object} order The order being edited.
 * @returns {Array<{ key: string, label: string, values: string[] }>} Dimension fields, sorted by key.
 */
export const quickAddDimensionFields = (order) =>
    Object.entries(getOrderCatalogScope(order))
        .map(([key, value]) => [key, `${value ?? ''}`.trim()])
        .filter(([key, value]) => key && value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => ({ key, label: dimensionLabel(key), values: [value] }));
